<script setup lang="ts">
import { PageProps } from '@/types'
import BankerAccountController from '@controllers/Banks/BankerAccountController'
import { useForm, usePage } from '@inertiajs/vue3'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BankerAccountData = App.Data.Account.BankerAccountData

const { t } = useI18n()
const page = usePage<PageProps>()

const props = defineProps<{
  user: BankerAccountData
  pendingEmailChange?: { new_email: string; expires_at?: string | null } | null
}>()

const visible = defineModel<boolean>('visible', { required: true })

const form = useForm({
  first_name: props.user.first_name,
  last_name: props.user.last_name,
  email: props.user.email,
  is_admin: props.user.is_admin,
})

const canManageRoles = computed(() => page.props.auth.user.user_permissions?.includes('manage client users') ?? false)

const isSelf = computed(() => props.user.id === page.props.auth.user.id)

const canEditRole = computed(() => canManageRoles.value && !isSelf.value)

const roleOptions = computed(() => [
  { label: t('roles.banker'), value: false },
  { label: t('roles.admin'), value: true },
])

watch(visible, (isOpen) => {
  if (!isOpen) {
    return
  }

  form.clearErrors()
  form.defaults({
    first_name: props.user.first_name,
    last_name: props.user.last_name,
    email: props.user.email,
    is_admin: props.user.is_admin,
  })
  form.reset()
})

function updateUser() {
  if (!props.user.id) {
    return
  }

  form.put(BankerAccountController.update({ bankerAccount: props.user.id }).url, {
    preserveScroll: true,
    onSuccess: () => {
      visible.value = false
    },
  })
}
</script>

<template>
  <Dialog v-model:visible="visible" modal :header="t('title')" class="w-full max-w-lg">
    <div class="mb-4 flex items-center gap-4">
      <label for="edit_first_name" class="min-w-24 font-semibold">{{ t('first_name') }}</label>
      <div class="flex-auto">
        <InputText
          id="edit_first_name"
          v-model="form.first_name"
          class="w-full"
          :invalid="!!form.errors.first_name"
          autofocus />
        <small v-if="form.errors.first_name" class="text-danger">
          {{ form.errors.first_name }}
        </small>
      </div>
    </div>

    <div class="mb-4 flex items-center gap-4">
      <label for="edit_last_name" class="min-w-24 font-semibold">{{ t('last_name') }}</label>
      <div class="flex-auto">
        <InputText id="edit_last_name" v-model="form.last_name" class="w-full" :invalid="!!form.errors.last_name" />
        <small v-if="form.errors.last_name" class="text-danger">
          {{ form.errors.last_name }}
        </small>
      </div>
    </div>

    <div class="mb-4 flex items-center gap-4">
      <label for="edit_email" class="min-w-24 font-semibold">{{ t('email') }}</label>
      <div class="flex-auto">
        <InputText id="edit_email" v-model="form.email" type="email" class="w-full" :invalid="!!form.errors.email" />
        <small v-if="form.errors.email" class="text-danger">
          {{ form.errors.email }}
        </small>
        <small v-else-if="pendingEmailChange" class="text-surface-500">
          {{ t('pending_email_change', { email: pendingEmailChange.new_email }) }}
        </small>
      </div>
    </div>

    <div v-if="canEditRole" class="mb-4 flex items-center gap-4">
      <span class="min-w-24 font-semibold">{{ t('role') }}</span>
      <div class="flex flex-row gap-4">
        <SelectButton
          v-model="form.is_admin"
          :options="roleOptions"
          option-label="label"
          option-value="value"
          :allow-empty="false"
          :aria-label="t('role')" />
        <small class="text-surface-500">{{ t('admin_hint') }}</small>
      </div>
    </div>

    <template #footer>
      <Button type="button" :label="t('cancel')" severity="secondary" @click="visible = false" />
      <Button type="button" :label="t('save')" :loading="form.processing" @click="updateUser" />
    </template>
  </Dialog>
</template>

<i18n>
{
  "en": {
    "title": "Edit User",
    "first_name": "First Name",
    "last_name": "Last Name",
    "email": "Email",
    "role": "Role",
    "roles": {
      "banker": "Banker",
      "admin": "Admin"
    },
    "admin_hint": "Admins can manage users of this bank.",
    "pending_email_change": "Pending confirmation for {email}",
    "cancel": "Cancel",
    "save": "Save"
  },
  "de": {
    "title": "Nutzer bearbeiten",
    "first_name": "Vorname",
    "last_name": "Nachname",
    "email": "E-Mail",
    "role": "Rolle",
    "roles": {
      "banker": "Banker",
      "admin": "Admin"
    },
    "admin_hint": "Admins können Nutzer dieser Bank verwalten.",
    "pending_email_change": "Bestätigung für {email} ausstehend",
    "cancel": "Abbrechen",
    "save": "Speichern"
  }
}
</i18n>
